import React, { useCallback, useEffect, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';

import { Button, LoadingDots, theme, useHovered, webOnlyStyles } from '@sg/garnish';

import { VerifyWithIdMeArtwork } from '../../assets';
import { ID_ME_BRAND_COLOR } from '../../IdMeVerificationScreen.constants';

// ─────────────────────────────────────────────────────────────────────────────

export const IdMeVerifyButton = (props: Props) => {
  const { isLoading = false, isDisabled = false, onPress } = props;

  const { formatMessage } = useIntl();

  // ─── Refs ──────────────────────────────────────────────────────────────────

  const buttonRef = useRef(null);

  // ─── Derived ───────────────────────────────────────────────────────────────

  const isHovered = useHovered(buttonRef);
  const isInactive = isLoading || isDisabled;
  const accessibilityState = { busy: isLoading };

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const renderContent = useCallback(
    () => <IdMeVerifyButtonContent isLoading={isLoading} />,
    [isLoading],
  );

  // ─── Styles ────────────────────────────────────────────────────────────────

  const isHoverVisible = isHovered && !isInactive;

  const buttonStyles = [
    styles.button,
    buttonWebStyles,
    isHoverVisible ? styles.buttonHovered : undefined,
    isDisabled ? styles.buttonDisabled : undefined,
  ];

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <Button
      ref={buttonRef}
      size="large-wide"
      palette="primary"
      disabled={isInactive}
      accessibilityLabel={formatMessage(messages.verifyWithIdMe)}
      accessibilityState={accessibilityState}
      style={buttonStyles}
      rightElement={renderContent}
      onPress={onPress}
    />
  );
};

// ─── Components ──────────────────────────────────────────────────────────────

// Web `entering`/`exiting` pop in part-way through, so both layers stay mounted and fade.
const IdMeVerifyButtonContent = (props: ContentProps) => {
  const { isLoading } = props;

  // ─── State ─────────────────────────────────────────────────────────────────

  const loadingProgress = useSharedValue(isLoading ? 1 : 0);

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const animateLoadingProgress = useCallback(() => {
    loadingProgress.value = withTiming(isLoading ? 1 : 0, LOADING_TIMING_CONFIG);
  }, [isLoading, loadingProgress]);

  // ─── Styles ────────────────────────────────────────────────────────────────

  const artworkAnimatedStyle = useAnimatedStyle(() => ({
    opacity: 1 - loadingProgress.value,
  }));
  const loadingAnimatedStyle = useAnimatedStyle(() => ({
    opacity: loadingProgress.value,
  }));
  const loadingStyles = [styles.loading, loadingAnimatedStyle];

  // ─── Effects ───────────────────────────────────────────────────────────────

  useEffect(animateLoadingProgress, [animateLoadingProgress]);

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <View>
      <Animated.View style={artworkAnimatedStyle}>
        <VerifyWithIdMeArtwork />
      </Animated.View>

      <Animated.View pointerEvents="none" style={loadingStyles}>
        <LoadingDots color={theme.colors.WHITE} />
      </Animated.View>
    </View>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  verifyWithIdMe: {
    defaultMessage: 'Verify with IDMe',
    description: 'ID.me | Branded verify button label',
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

const LOADING_TIMING_CONFIG = {
  duration: theme.transitions.base,
  easing: Easing.inOut(Easing.ease),
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  button: {
    paddingHorizontal: theme.spacing['8'],
    backgroundColor: ID_ME_BRAND_COLOR,
    borderColor: '#118834',
  },
  buttonHovered: {
    backgroundColor: '#0E6A2D',
  },
  buttonDisabled: {
    opacity: 0.5,
  },
  loading: {
    ...StyleSheet.absoluteFill,
    alignItems: 'center',
    justifyContent: 'center',
  },
});

const buttonWebStyles = webOnlyStyles({
  transition: theme.transitions.presets.opacity,
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  isLoading?: boolean;
  isDisabled?: boolean;
  onPress?: () => void;
};

type ContentProps = Required<Pick<Props, 'isLoading'>>;
